import { ThunkDispatch } from 'redux-thunk';

import { TAppError } from '@/store/types/base.state';
import { ProfileActionTypes, TProfileAction } from '@/store/types/profile.types';
import { TCoreAction } from '@/store/types/modals.types';

import { ITaxResidence, ITaxResidenceBase, ITaxResidenceUpdate } from '@/store/models/taxResidence.model';

import { closeModal } from '@/store/actions/core.actions';

import { TTaxResidenceGroupAction, taxResidenceTypes } from '@/features/taxResidences/store/tax-residence.types';
import { TaxResidenceService } from '@/features/taxResidences/services/TaxResidenceService';

export const getTaxResidence = (userId: number) => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.GET_TAX_RESIDENCE,
    userId,
  });

  const success = (taxResidences: ITaxResidence[]): TProfileAction => ({
    type: ProfileActionTypes.GET_TAX_RESIDENCE_SUCCESS,
    userId,
    taxResidences,
    shouldSkipToaster: true,
  });

  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_TAX_RESIDENCE_FAIL,
    userId,
    error,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const taxResidences = await TaxResidenceService.getTaxResidences(userId);
      dispatch(success(taxResidences));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export function addTaxResidence(
  taxResidence: ITaxResidenceBase,
  batchIndex?: string,
  shouldSkipToasterOnSuccess?: boolean,
) {
  function request(): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.ADD_TAX_RESIDENCE,
      userId: taxResidence.userId,
      batchIndex,
    };
  }

  function success(taxResidence: ITaxResidenceBase): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.ADD_TAX_RESIDENCE_SUCCESS,
      userId: taxResidence.userId,
      taxResidence,
      batchIndex,
      shouldSkipToaster: shouldSkipToasterOnSuccess,
    };
  }

  function fail(error: TAppError): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.ADD_TAX_RESIDENCE_FAIL,
      userId: taxResidence.userId,
      error,
      batchIndex,
    };
  }

  return async (dispatch: ThunkDispatch<{}, {}, TTaxResidenceGroupAction>): Promise<void> => {
    dispatch(request());
    try {
      const response = await TaxResidenceService.addTaxResidence(taxResidence, batchIndex);
      dispatch(success(response));
    } catch (error) {
      dispatch(fail(error));
    }
  };
}

export function updateTaxResidence(taxResidence: ITaxResidenceBase) {
  function request(): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.UPDATE_TAX_RESIDENCE,
      userId: taxResidence.userId,
      taxResidenceId: taxResidence.id,
    };
  }

  function success(taxResidence: ITaxResidence): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.UPDATE_TAX_RESIDENCE_SUCCESS,
      userId: taxResidence.userId,
      taxResidence,
    };
  }

  function fail(error: TAppError): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.UPDATE_TAX_RESIDENCE_FAIL,
      userId: taxResidence.userId,
      error,
      taxResidenceId: taxResidence.id,
    };
  }

  return async (dispatch: ThunkDispatch<{}, {}, TTaxResidenceGroupAction>): Promise<void> => {
    dispatch(request());
    try {
      const response = await TaxResidenceService.updateTaxResidence(taxResidence);
      dispatch(success(response));
    } catch (error) {
      dispatch(fail(error));
    }
  };
}

export function deleteTaxResidence(userId: number, taxResidenceId: number) {
  function request(): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.DELETE_TAX_RESIDENCE,
      userId,
      taxResidenceId,
    };
  }

  function success(): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.DELETE_TAX_RESIDENCE_SUCCESS,
      userId,
      taxResidenceId,
    };
  }

  function fail(error: TAppError): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.DELETE_TAX_RESIDENCE_FAIL,
      userId,
      taxResidenceId,
      error,
    };
  }

  return async (dispatch: ThunkDispatch<{}, {}, TTaxResidenceGroupAction>): Promise<void> => {
    dispatch(request());
    try {
      await TaxResidenceService.deleteTaxResidence(taxResidenceId);
      dispatch(success());
    } catch (error) {
      dispatch(fail(error));
    }
  };
}

export function updateTaxResidences(userId: number, taxResidences: ITaxResidenceUpdate[], onFinish?: () => void) {
  return async (dispatch: ThunkDispatch<{}, {}, TTaxResidenceGroupAction | TCoreAction>): Promise<void> => {
    try {
      taxResidences.map(({ isDeleted, isUpdated, ...taxResidence }) => {
        if (!taxResidence.id) dispatch(addTaxResidence({ userId, ...taxResidence }));
        if (isUpdated) dispatch(updateTaxResidence({ userId, ...taxResidence }));
        if (isDeleted) dispatch(deleteTaxResidence(userId, taxResidence.id));
      });
    } catch (error) {
      console.error('Error updating tax residences:', error);
    } finally {
      dispatch(closeModal());
      onFinish?.();
    }
  };
}

export function addChildTaxResidence(
  parentId: number,
  taxResidence: ITaxResidenceBase,
  batchIndex?: string,
  shouldSkipToasterOnSuccess?: boolean,
) {
  function request(): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.ADD_CHILD_TAX_RESIDENCE,
      userId: taxResidence.userId,
      batchIndex,
      parentId,
    };
  }

  function success(taxResidence: ITaxResidenceBase): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.ADD_CHILD_TAX_RESIDENCE_SUCCESS,
      userId: taxResidence.userId,
      taxResidence,
      batchIndex,
      shouldSkipToaster: shouldSkipToasterOnSuccess,
      parentId,
    };
  }

  function fail(error: TAppError): TTaxResidenceGroupAction {
    return {
      type: taxResidenceTypes.ADD_CHILD_TAX_RESIDENCE_FAIL,
      userId: taxResidence.userId,
      error,
      batchIndex,
      parentId,
    };
  }

  return async (dispatch: ThunkDispatch<{}, {}, TTaxResidenceGroupAction>): Promise<void> => {
    dispatch(request());
    try {
      const response = await TaxResidenceService.addTaxResidence(taxResidence, batchIndex);
      dispatch(success(response));
    } catch (error) {
      dispatch(fail(error));
    }
  };
}
